import React, { useEffect, useState } from 'react';
import { collection, getDocs, updateDoc, doc, query, where, Timestamp, deleteDoc } from 'firebase/firestore'; // Import deleteDoc
import { db, updateBahanStockOnApproval } from '../../firebase'; // Import the stock update function
import './PersetujuanPermintaanBahan.css'; // Import the CSS file

// Helper to format Firestore Timestamp to readable date string
const formatDate = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toLocaleDateString('id-ID'); // Indonesian locale
  }
  return typeof timestamp === 'string' ? timestamp : 'N/A';
};

const PersetujuanPermintaanBahan = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState(null); // Track which request is being updated

  // Fetch pending, approved, and cancelled requests
  const fetchRequests = async () => { // Renamed function
    setLoading(true);
    setError('');
    try {
      const requestsRef = collection(db, 'permintaan_bahan');
      // Query for requests where status is 'pending', 'approved', or 'cancelled'
      const q = query(requestsRef, where('status', 'in', ['pending', 'approved', 'cancelled']));
      const querySnapshot = await getDocs(q);
      let data = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));

      // Sort requests: pending first, then by request date descending
      const statusOrder = { pending: 1, approved: 2, cancelled: 3 };
      data.sort((a, b) => {
        const orderA = statusOrder[a.status] || 99;
        const orderB = statusOrder[b.status] || 99;
        if (orderA !== orderB) {
          return orderA - orderB; // Sort by status order first
        }
        // If status is the same, sort by request date descending (newest first)
        const timeA = a.tanggalPermintaan?.seconds || 0;
        const timeB = b.tanggalPermintaan?.seconds || 0;
        return timeB - timeA;
      });
      setRequests(data);
    } catch (err) {
      console.error("Error fetching material requests:", err); // Updated error message
      setError('Gagal memuat data permintaan bahan.');
    } finally {
      setLoading(false);
    }
  };

  // Handle status update (Approve/Reject)
  const handleUpdateRequestStatus = async (id, newStatus) => {
    setUpdatingId(id); // Set loading state for this specific request's buttons
    setError('');
    try {
      const requestRef = doc(db, 'permintaan_bahan', id);
      const requestToUpdate = requests.find(req => req.id === id); // Find the request details

      if (!requestToUpdate) {
        throw new Error("Request details not found locally.");
      }

      await updateDoc(requestRef, { status: newStatus });
      console.log(`Material Request ${id} status updated to ${newStatus}.`);

      // Decrease stock if approved
      if (newStatus === 'approved') {
        try {
          await updateBahanStockOnApproval(
            requestToUpdate.namaBahan,
            requestToUpdate.jumlahDiminta,
            requestToUpdate.satuan
          );
          console.log(`Stock deduction initiated for approved request ${id}.`);
        } catch (stockError) {
          console.error(`Failed to update stock for request ${id}:`, stockError);
          // Optionally: revert status update or notify admin/laboran
          setError(`Status permintaan ${id} diperbarui, tetapi gagal mengurangi stok: ${stockError.message}`);
          // Do not refetch yet, keep the item visible with the error
          setUpdatingId(null); // Reset loading state even on stock error
          return; // Stop further execution in this handler
        }
      }

      // Refetch requests to update the list *after* successful status update and stock deduction (if applicable)
      fetchRequests();

    } catch (err) {
      console.error(`Error updating material request ${id} status to ${newStatus}:`, err); // Added newStatus to error log
      setError(`Gagal memperbarui status permintaan ${id}: ${err.message}`);
    } finally {
      setUpdatingId(null); // Reset loading state
    }
  };

  // Handle deleting a request
  const handleDeleteRequest = async (id) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus permintaan ini secara permanen?')) {
      return;
    }
    setUpdatingId(id); // Indicate loading/processing
    setError('');
    try {
      const requestRef = doc(db, 'permintaan_bahan', id);
      await deleteDoc(requestRef);
      console.log(`Material Request ${id} deleted successfully.`);
      // Refetch requests to update the list
      fetchRequests();
    } catch (err) {
      console.error(`Error deleting material request ${id}:`, err);
      setError(`Gagal menghapus permintaan ${id}.`);
    } finally {
      setUpdatingId(null); // Reset loading state
    }
  };

  // Initial fetch and refetch mechanism
  useEffect(() => {
    fetchRequests(); // Use the renamed function
  }, []);

  return (
    <div className="persetujuan-bahan-container">
      <h2>Persetujuan Permintaan Bahan</h2>

      {error && <div className="error-message">{error}</div>}

      {loading && !requests.length ? ( // Show loading only on initial load
        <div className="loading-message">Memuat data permintaan...</div>
      ) : (
        <div className="persetujuan-bahan-table-container">
          <table className="persetujuan-bahan-table">
            <thead>
              <tr>
                <th>Nama Peminta</th>
                <th>Nama Bahan</th>
                <th>Jumlah Diminta</th>
                <th>Satuan</th>
                <th>Tgl Permintaan</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.id}>
                  <td>{req.namaUser || 'N/A'}</td>
                  <td>{req.namaBahan || 'N/A'}</td>
                  <td>{req.jumlahDiminta || 'N/A'}</td>
                  <td>{req.satuan || 'N/A'}</td>
                  <td>{formatDate(req.tanggalPermintaan)}</td>
                  <td>
                    <span className={`status-badge status-${req.status}`}>
                      {req.status}
                    </span>
                  </td>
                  <td className="action-buttons">
                    {req.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleUpdateRequestStatus(req.id, 'approved')}
                          className="btn btn-approve"
                          disabled={updatingId === req.id}
                        >
                          {updatingId === req.id ? '...' : 'Setujui'}
                        </button>
                        <button
                          onClick={() => handleUpdateRequestStatus(req.id, 'rejected')}
                          className="btn btn-reject"
                          disabled={updatingId === req.id}
                        >
                          {updatingId === req.id ? '...' : 'Tolak'}
                        </button>
                      </>
                    )}
                     {req.status === 'approved' && (
                      <button
                        onClick={() => handleUpdateRequestStatus(req.id, 'cancelled')}
                        className="btn btn-cancel"
                        disabled={updatingId === req.id}
                      >
                        {updatingId === req.id ? '...' : 'Batalkan'}
                      </button>
                    )}
                    {req.status === 'cancelled' && (
                      <>
                        <button
                          onClick={() => handleUpdateRequestStatus(req.id, 'approved')}
                          className="btn btn-approve"
                          style={{ marginRight: '5px' }}
                          disabled={updatingId === req.id}
                        >
                          {updatingId === req.id ? '...' : 'Setujui Ulang'}
                        </button>
                        <button
                          onClick={() => handleDeleteRequest(req.id)}
                          className="btn btn-delete" // Ensure .btn-delete style exists or use .btn-reject
                          disabled={updatingId === req.id}
                        >
                          {updatingId === req.id ? '...' : 'Hapus'}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {requests.length === 0 && !loading && (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center' }}>Tidak ada permintaan bahan yang perlu diproses atau dibatalkan.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PersetujuanPermintaanBahan;
